import React, { useCallback } from 'react';
import { FC131 } from '../../../services/c205Api';

interface DetailTableProps {
  details: Partial<FC131>[];
  selectedRow: number;
  isEditing: boolean;
  setSelectedRow: (idx: number) => void;
  setDetails: React.Dispatch<React.SetStateAction<Partial<FC131>[]>>;
  addDetailRow: () => void;
  deleteDetailRow: () => void;
}

/** Format number with locale grouping, max 2 decimals */
const fmt = (v: number | null | undefined): string => {
  if (v == null || v === 0) return '0';
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const DetailTable: React.FC<DetailTableProps> = ({
  details, selectedRow, isEditing,
  setSelectedRow, setDetails,
  addDetailRow, deleteDetailRow,
}) => {
  const handleCellChange = useCallback((rowIdx: number, key: keyof FC131, value: string) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[rowIdx]) {
        if (key === 'fc131_16') {
          next[rowIdx] = { ...next[rowIdx], [key]: parseFloat(value) || 0 };
        } else {
          next[rowIdx] = { ...next[rowIdx], [key]: value };
        }
      }
      return next;
    });
  }, [setDetails]);

  const selDetail = details[selectedRow] as Partial<FC131> | undefined;

  return (
    <div className="c205-detail-section">

      {/* Action buttons for detail rows */}
      {isEditing && (
        <div className="detail-actions">
          <button className="btn btn-sm" onClick={addDetailRow}>明細新增</button>
          <button className="btn btn-sm btn-danger" onClick={deleteDetailRow}
            disabled={details.length <= 0}>明細刪除</button>
        </div>
      )}

      {/* Detail Grid — column order matches FoxPro contract */}
      <div className="detail-table-wrapper">
        <table className="detail-table">
          <thead>
            <tr>
              <th style={{ width: 35 }}>項次</th>
              <th style={{ width: 165 }} data-contract-field="fc131_11">產品代號</th>
              <th style={{ width: 82, textAlign: 'right' }} data-contract-field="fc131_151">S/C 含稅單價</th>
              <th style={{ width: 84, textAlign: 'right' }}>S/C 未稅單價</th>
              <th style={{ width: 84, textAlign: 'right' }} data-contract-field="fc131_16">退回數量</th>
              <th style={{ width: 70, textAlign: 'right' }} data-contract-field="fc131_28">交運數量</th>
              <th style={{ width: 35 }} data-contract-field="fc131_20">單位</th>
              <th style={{ width: 100, textAlign: 'right' }} data-contract-field="fc131_16">未稅退回金額</th>
            </tr>
          </thead>
          <tbody>
            {details.map((d, i) => (
              <tr key={i}
                className={i === selectedRow ? 'selected' : ''}
                onClick={() => setSelectedRow(i)}>
                <td style={{ textAlign: 'center' }}>{d.fc131_02 ?? i + 1}</td>
                <td>{d.fc131_11 || ''}</td>
                <td style={{ textAlign: 'right' }}>{fmt(d.fc131_151)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(d.fc131_15)}</td>
                <td style={{ textAlign: 'right' }}>
                  {isEditing ? (
                    <input type="number" step="0.01"
                      style={{ width: '100%', textAlign: 'right' }}
                      value={d.fc131_16 ?? ''}
                      onChange={e => handleCellChange(i, 'fc131_16', e.target.value)} data-contract-field="fc131_16"
                    />
                  ) : fmt(d.fc131_16)}
                </td>
                <td style={{ textAlign: 'right' }}>{fmt(d.fc131_28)}</td>
                <td>{d.fc131_20 || ''}</td>
                <td style={{ textAlign: 'right' }}>{fmt(Math.round(((d.fc131_15 || 0) * (d.fc131_16 || 0)) * 100) / 100)}</td>
              </tr>
            ))}
            {details.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: '#999' }}>無明細資料</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Detail Panel: always visible — mirrors FoxPro layout */}
      <div className="detail-panel">
        <div className="detail-panel-title">
          {selDetail ? `選取明細 (項次 ${selDetail.fc131_02})` : '選取明細'}
        </div>

        {/* Group 1: 產品資訊 — 色號 + 中文色名 + 英文色名 */}
        <div className="form-grid" style={{ marginBottom: 12 }}>
          <label data-contract-field="fc131_14">色號</label>
          <div>{selDetail?.fc131_14 || '--'}</div>

          <label data-contract-field="fc131_18">中文色名</label>
          <div>{selDetail?.fc131_18 || '--'}</div>

          <label data-contract-field="fc131_19">英文色名</label>
          <div>{selDetail?.fc131_19 || '--'}</div>
        </div>

        {/* Group 2: 交運 — framed section matching FoxPro Shape3 */}
        <div className="form-section">
          <span className="section-tag">交運</span>
          <div className="form-grid">
            <label data-contract-field="fc131_26">庫存單位</label>
            <div>{selDetail?.fc131_26 || '--'}</div>

            <label>&nbsp;</label>
            <div>&nbsp;</div>

            <label data-contract-field="fc131_23">交運批號</label>
            <div>{selDetail?.fc131_23 || '--'}</div>

            <label data-contract-field="fc131_17">批號單位換算率</label>
            <div>{selDetail ? fmt(selDetail.fc131_17) : '--'}</div>

            <label data-contract-field="fc131_24">交運單項次</label>
            <div>{selDetail?.fc131_24 ?? '--'}</div>

            <label data-contract-field="fc131_22">交運批號項次</label>
            <div>{selDetail?.fc131_22 ?? '--'}</div>

            <label data-contract-field="fc131_13">S/C 項次</label>
            <div>{selDetail?.fc131_13 ?? '--'}</div>

            <label data-contract-field="fc131_25">S/C 顏色項次</label>
            <div>{selDetail?.fc131_25 ?? '--'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(DetailTable);
